import qs from 'qs';
import { useEffect } from 'react';

import useStorage from 'hooks/useStorage';
import { COMMA_LIST_PARSE_OPTIONS } from 'utils/url';

const defaultQueryParseOpts = {
  persistOnUrl: true,
  typeCoercion: true,
};

interface UseQueryParamsOptions {
  persistOnUrl?: boolean;
  typeCoercion?: boolean;
  rebuildUrlOnSet?: boolean;
  initialQueryString?: string;
}

const filterDefaultValues = (
  obj: Record<string, any>,
  defaultObj: Record<string, any>,
) => {
  const filtered: Record<string, any> = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key] !== defaultObj[key]) {
      filtered[key] = obj[key];
    }
  });
  return filtered;
};

export default function useQueryParams<T>(
  key: string,
  defaultValue: T,
  options: UseQueryParamsOptions = defaultQueryParseOpts,
) {
  const mergedOpts = {
    ...defaultQueryParseOpts,
    ...(options || /* istanbul ignore next -- @preserve */ {}),
  };

  const storedValues = useStorage(key, defaultValue, mergedOpts);

  useEffect(() => {
    if (mergedOpts.persistOnUrl && mergedOpts.rebuildUrlOnSet) {
      const currentUrlParams = qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
        ...COMMA_LIST_PARSE_OPTIONS,
        decoder: mergedOpts.typeCoercion
          ? /* istanbul ignore next -- @preserve */ undefined
          : (str: string) => str,
      });
      const mergedParams = {
        ...currentUrlParams,
        ...storedValues[0],
      };
      const filteredValues = filterDefaultValues(
        mergedParams,
        defaultValue as Record<string, any>,
      );
      const newUrlParams = qs.stringify(filteredValues, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });
      const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${newUrlParams}`;
      window.history.replaceState({ path: newUrl }, '', newUrl);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return storedValues;
}
